import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import EmptyState from "../../components/EmptyState";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Plus, ScrollText, FileSignature } from "lucide-react";

const STATUS_PILLS = {
  draft: { color: "bg-slate-100 text-slate-600", label: "Draft" },
  submitted: { color: "bg-blue-100 text-blue-700", label: "Submitted · Awaiting" },
  accepted: { color: "bg-emerald-100 text-emerald-700", label: "Accepted" },
  rejected: { color: "bg-red-100 text-red-700", label: "Rejected" },
  needs_revision: { color: "bg-amber-100 text-amber-700", label: "Needs Revision" },
};

export default function MyResolutionsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/resolutions/me").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto pb-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">My Resolutions</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Draft resolutions
            </h1>
          </div>
          <Link to="/dashboard/resolutions/new">
            <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="new-resolution-button">
              <Plus className="h-4 w-4 mr-1" /> New resolution
            </Button>
          </Link>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="resolutions-list">
          {loading ? (
            <p className="col-span-2 text-sm text-slate-500">Loading…</p>
          ) : items.length === 0 ? (
            <div className="col-span-2">
              <EmptyState
                icon={FileSignature}
                eyebrow="No resolutions yet"
                title="Draft your first resolution"
                description="Start with the preamble, add operative clauses, and let Diplomexa AI sharpen wording and structure for the chair's review."
                primary={{ label: "Start drafting", testId: "empty-start-resolution", onClick: () => navigate("/dashboard/resolutions/new") }}
                testId="resolutions-empty-state"
              />
            </div>
          ) : items.map((r) => {
            const reviewKey = r.chair_review_status === "pending" ? "submitted" : r.chair_review_status;
            const pill = r.status === "submitted"
              ? (STATUS_PILLS[reviewKey] || STATUS_PILLS.submitted)
              : STATUS_PILLS.draft;
            return (
              <Link
                key={r.id}
                to={`/dashboard/resolutions/${r.id}`}
                className="rounded-xl bg-white border border-slate-200 p-5 hover:-translate-y-0.5 hover:shadow-md transition-all"
                data-testid={`resolution-card-${r.id}`}
              >
                <div className="flex items-start justify-between">
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${pill.color}`}>
                    {pill.label}
                  </span>
                  <span className="text-[10px] text-slate-400 font-mono">v{r.version}</span>
                </div>
                <h3 className="mt-3 text-base font-semibold tracking-tight text-[#0A192F]">{r.title}</h3>
                <p className="mt-1 text-xs text-slate-500">
                  {r.preambulatory?.length || 0} preambulatory · {r.operative?.length || 0} operative · {r.co_sponsors?.length || 0} sponsors
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
